/**
 * IP Address utility — fetches and caches the user's public IP address.
 *
 * Uses ipify.org's free API to get the user's IP. The IP is cached in memory
 * for the session to avoid repeated API calls.
 */

let cachedIP = null;

/**
 * Fetches the user's public IP address.
 * Returns cached IP if already fetched, otherwise makes API call.
 * Falls back to 'unknown' if fetch fails.
 *
 * @returns {Promise<string>} The user's IP address or 'unknown'
 */
export async function getUserIP() {
  // Return cached IP if available
  if (cachedIP) {
    return cachedIP;
  }

  try {
    const response = await fetch('https://api.ipify.org?format=json', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    cachedIP = data.ip || 'unknown';
    return cachedIP;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[ipAddress] Failed to fetch user IP:', error.message);
    }
    cachedIP = 'unknown';
    return cachedIP;
  }
}

/**
 * Clears the cached IP address (useful for testing).
 */
export function clearIPCache() {
  cachedIP = null;
}
